import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, BedDouble, Bath, Users, MapPin, Check, CalendarDays, Tag } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtINR, toISO, prettyDate } from "../lib/dates";
import { loadRazorpay } from "../lib/razorpay";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";

export default function VillaDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [villa, setVilla] = useState(null);
  const [disabledDates, setDisabledDates] = useState([]);
  const [range, setRange] = useState(undefined);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: searchParams.get("guests") || "2", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [consent, setConsent] = useState({ data: false, marketing: false, terms: false });
  const [paymentOption, setPaymentOption] = useState("deposit");
  const [calMonth, setCalMonth] = useState(new Date());

  useEffect(() => {
    if (range?.from) setCalMonth(range.from);
  }, [range?.from]);

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => setVilla(data)).catch(() => toast.error("Villa not found"));
    // fetch inventory for next ~8 months to disable unavailable dates
    const start = toISO(new Date());
    const endD = new Date();
    endD.setMonth(endD.getMonth() + 8);
    api.get(`/inventory/${id}`, { params: { start, end: toISO(endD) } })
      .then(({ data }) => {
        const blocked = (data.days || [])
          .filter((d) => d.status === "blocked" || d.status === "booked")
          .map((d) => new Date(d.date + "T00:00:00"));
        setDisabledDates(blocked);
      })
      .catch(() => {});
  }, [id]);

  // Pre-fill from query dates
  useEffect(() => {
    const ci = searchParams.get("check_in");
    const co = searchParams.get("check_out");
    if (ci && co) setRange({ from: new Date(ci + "T00:00:00"), to: new Date(co + "T00:00:00") });
  }, [searchParams]);

  useEffect(() => {
    if (range?.from && range?.to) {
      setQuoting(true);
      api.get(`/inventory/${id}/quote`, { params: { check_in: toISO(range.from), check_out: toISO(range.to), code: appliedCode } })
        .then(({ data }) => setQuote(data))
        .catch((e) => { setQuote(null); toast.error(formatApiErrorDetail(e.response?.data?.detail)); })
        .finally(() => setQuoting(false));
    } else {
      setQuote(null);
    }
  }, [range, id, appliedCode]);

  const applyCode = () => {
    if (!codeInput.trim()) return;
    if (!range?.from || !range?.to) return toast.error("Select dates first.");
    setAppliedCode(codeInput.trim().toUpperCase());
  };

  const disabled = useMemo(() => [{ before: new Date(new Date().setHours(0, 0, 0, 0)) }, ...disabledDates], [disabledDates]);

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", guests: "2", note: "" });
    setRange(undefined);
    setAppliedCode("");
    setCodeInput("");
    setConsent({ data: false, marketing: false, terms: false });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) return toast.error("Please select check-in and check-out dates.");
    if (!form.name || !form.email || !form.phone) return toast.error("Please fill your contact details.");
    if (!consent.data || !consent.terms) return toast.error("Please accept the data consent and Terms & Conditions.");
    if (quote && !quote.available) return toast.error("Selected dates are not available.");
    setSubmitting(true);
    try {
      // 1. create the booking (pending, unpaid)
      const { data: booking } = await api.post(`/inventory/${id}/request`, {
        check_in: toISO(range.from),
        check_out: toISO(range.to),
        guests: Number(form.guests) || 1,
        name: form.name,
        email: form.email,
        phone: form.phone,
        note: form.note,
        offer_code: appliedCode,
        consent_data: consent.data,
        consent_marketing: consent.marketing,
        consent_terms: consent.terms,
      });

      // 2. create a Razorpay order for the chosen payment option
      const { data: order } = await api.post(`/bookings/${booking.id}/create-order`, {
        payment_option: paymentOption,
      });

      // 3. open Razorpay checkout
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway");

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "LAVista Stays.com",
        description: `${paymentOption === "deposit" ? "20% Deposit" : "Full Payment"} · ${villa.title}`,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#2C4A3B" },
        handler: async (res) => {
          try {
            await api.post(`/payments/verify`, {
              booking_id: booking.id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful! Your booking is confirmed.");
            resetForm();
          } catch {
            toast.error("Payment could not be verified. Please contact support.");
          }
        },
        modal: { ondismiss: () => toast.message("Payment cancelled. Your booking is held as pending.") },
      });
      rzp.open();
    } catch (e2) {
      const detail = formatApiErrorDetail(e2.response?.data?.detail);
      toast.error(detail || e2.message || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  const depositAmount = quote ? Math.round(quote.total * 0.2) : 0;

  if (!villa) return <div className="min-h-[70vh] flex items-center justify-center text-muted-foreground">Loading villa...</div>;

  const gallery = villa.gallery?.length ? villa.gallery : [villa.image];

  return (
    <div data-testid="villa-detail-page" className="bg-secondary/30">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-light">{villa.title}</h1>
            <p className="flex items-center gap-1.5 text-muted-foreground mt-2">
              <MapPin className="h-4 w-4" /> {villa.location}
              {villa.rating && (
                <span className="ml-3 inline-flex items-center gap-1 text-foreground">
                  <Star className="h-4 w-4 text-accent fill-accent" /> {villa.rating}
                </span>
              )}
            </p>
          </div>
          <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-full">{villa.type}</span>
        </div>

        {/* Gallery */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-6">
          <div className="lg:col-span-2 rounded-lg overflow-hidden h-[300px] sm:h-[440px]">
            <img src={gallery[activeImg]} alt={villa.title} className="h-full w-full object-cover" data-testid="villa-main-image" />
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
            {gallery.slice(0, 3).map((g, i) => (
              <button key={i} data-testid={`villa-thumb-${i}`} onClick={() => setActiveImg(i)}
                className={`rounded-lg overflow-hidden h-24 lg:h-[140px] border-2 transition-colors ${activeImg === i ? "border-accent" : "border-transparent"}`}>
                <img src={g} alt={`view ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Content + Booking */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-6 text-foreground/80">
              <span className="flex items-center gap-2"><Users className="h-5 w-5 text-accent" /> Up to {villa.guests} guests</span>
              <span className="flex items-center gap-2"><BedDouble className="h-5 w-5 text-accent" /> {villa.bedrooms} bedrooms</span>
              <span className="flex items-center gap-2"><Bath className="h-5 w-5 text-accent" /> {villa.baths} baths</span>
            </div>
            <Separator className="my-6" />
            <h2 className="font-heading text-2xl font-light">About this villa</h2>
            <p className="text-muted-foreground leading-relaxed mt-3">{villa.description}</p>

            <h2 className="font-heading text-2xl font-light mt-8">Amenities</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              {villa.amenities?.map((a) => (
                <span key={a} className="flex items-center gap-2 text-sm text-foreground/80">
                  <Check className="h-4 w-4 text-primary" /> {a}
                </span>
              ))}
            </div>

            <h2 className="font-heading text-2xl font-light mt-8 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-accent" /> Availability
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Select your check-in and check-out dates. Greyed-out dates are unavailable.</p>
            <div className="mt-4 bg-card border border-border rounded-lg p-4 w-fit" data-testid="villa-calendar">
              <Calendar mode="range" month={calMonth} onMonthChange={setCalMonth} selected={range} onSelect={setRange} numberOfMonths={1} disabled={disabled} />
            </div>
          </div>

          {/* Booking card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="booking-card">
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-3xl text-accent">{fmtINR(villa.base_rate)}</span>
                <span className="text-sm text-muted-foreground">/ night onwards</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Rates vary by season & weekend (dynamic pricing).</p>

              <form onSubmit={submit} className="mt-5 space-y-4" data-testid="booking-form">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="border border-border rounded-md p-2.5">
                    <div className="text-xs text-muted-foreground">Check-in</div>
                    <div className="font-medium" data-testid="booking-checkin-display">{range?.from ? prettyDate(toISO(range.from)) : "Select"}</div>
                  </div>
                  <div className="border border-border rounded-md p-2.5">
                    <div className="text-xs text-muted-foreground">Check-out</div>
                    <div className="font-medium" data-testid="booking-checkout-display">{range?.to ? prettyDate(toISO(range.to)) : "Select"}</div>
                  </div>
                </div>

                {quoting && <p className="text-sm text-muted-foreground" data-testid="quote-loading">Calculating price...</p>}
                {quote && (
                  <div className="space-y-2 text-sm border-t border-border pt-4" data-testid="quote-breakdown">
                    <div className="flex justify-between"><span>{fmtINR(villa.base_rate)}+ × {quote.num_nights} nights</span><span>{fmtINR(quote.subtotal)}</span></div>
                    {quote.discount > 0 && (
                      <div className="flex justify-between text-primary" data-testid="quote-discount"><span>Discount ({quote.offer_code})</span><span>- {fmtINR(quote.discount)}</span></div>
                    )}
                    <div className="flex justify-between text-muted-foreground"><span>Taxes & fees (18%)</span><span>{fmtINR(quote.taxes)}</span></div>
                    <div className="flex justify-between font-heading text-lg pt-2 border-t border-border"><span>Total</span><span data-testid="quote-total">{fmtINR(quote.total)}</span></div>
                    <div className="flex justify-between text-xs text-muted-foreground"><span>+ Refundable security deposit</span><span>{fmtINR(quote.security_deposit)}</span></div>
                    {!quote.available && <p className="text-destructive text-xs">Some selected dates are unavailable.</p>}
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5 text-xs"><Tag className="h-3.5 w-3.5 text-accent" /> Offer code</Label>
                  <div className="flex gap-2">
                    <Input data-testid="offer-code-input" value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="e.g. MONSOON20" className="h-9" />
                    <Button type="button" variant="outline" size="sm" data-testid="apply-offer-btn" onClick={applyCode} className="h-9">Apply</Button>
                  </div>
                  {appliedCode && quote?.offer_code && <p className="text-xs text-primary" data-testid="offer-applied">Applied: {quote.offer_title}</p>}
                  {appliedCode && quote && !quote.offer_code && <p className="text-xs text-destructive" data-testid="offer-invalid">Code not valid for these dates.</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="b-guests">Guests</Label>
                  <Input id="b-guests" type="number" min="1" max={villa.guests} data-testid="booking-guests-input" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="b-name">Full name</Label>
                  <Input id="b-name" data-testid="booking-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="b-email">Email</Label>
                    <Input id="b-email" type="email" data-testid="booking-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="b-phone">Phone</Label>
                    <Input id="b-phone" data-testid="booking-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 ..." />
                  </div>
                </div>

                <div className="space-y-3 border-t border-border pt-4">
                  <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                    <Checkbox data-testid="consent-data-checkbox" checked={consent.data} onCheckedChange={(v) => setConsent({ ...consent, data: !!v })} className="mt-0.5" />
                    <span>I consent to LAVista using my personal data for billing and to send me marketing offers &amp; discounts. <span className="text-destructive">*</span></span>
                  </label>
                  <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                    <Checkbox data-testid="consent-terms-checkbox" checked={consent.terms} onCheckedChange={(v) => setConsent({ ...consent, terms: !!v })} className="mt-0.5" />
                    <span>I have read and agree to the{" "}
                      <Link to="/terms" target="_blank" className="text-accent underline" data-testid="terms-link">Terms &amp; Conditions</Link>. <span className="text-destructive">*</span></span>
                  </label>
                </div>

                {quote && quote.available && (
                  <div className="space-y-2 border-t border-border pt-4" data-testid="payment-options">
                    <Label className="text-xs">Payment option</Label>
                    <button type="button" data-testid="pay-deposit-option" onClick={() => setPaymentOption("deposit")}
                      className={`w-full text-left rounded-md border p-3 transition-colors ${paymentOption === "deposit" ? "border-accent bg-accent/5" : "border-border"}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Pay 20% deposit</span>
                        <span className="font-heading text-lg text-accent">{fmtINR(depositAmount)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Balance payable within 7 days of booking.</p>
                    </button>
                    <button type="button" data-testid="pay-full-option" onClick={() => setPaymentOption("full")}
                      className={`w-full text-left rounded-md border p-3 transition-colors ${paymentOption === "full" ? "border-accent bg-accent/5" : "border-border"}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Pay in full</span>
                        <span className="font-heading text-lg text-accent">{fmtINR(quote.total)}</span>
                      </div>
                    </button>
                    <p className="text-xs text-muted-foreground">Refundable security deposit of {fmtINR(quote.security_deposit)} collected at check-in.</p>
                  </div>
                )}

                <Button type="submit" data-testid="booking-submit-btn" disabled={submitting || (quote && !quote.available) || !consent.data || !consent.terms} className="w-full rounded-full h-12 text-base">
                  {submitting ? "Processing..." : quote ? `Reserve & Pay ${fmtINR(paymentOption === "deposit" ? depositAmount : quote.total)}` : "Reserve & Pay"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">Secure payment via Razorpay. Cancellation &amp; refund per our Terms.</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
